import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import BarcodeScanner from "../components/BarcodeScanner";
import {
  Package,
  Loader2,
  Search,
  Plus,
  Edit,
  Trash2,
  Box,
  AlertTriangle,
  Filter,
  X,
  Barcode,
  Upload,
  FileSpreadsheet,
  Check,
  ArrowRight,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  RotateCcw
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

const UNIDADES_MEDIDA = [
  { value: "unidad", label: "Unidad" },
  { value: "kg", label: "Kilogramo (kg)" },
  { value: "g", label: "Gramo (g)" },
  { value: "lb", label: "Libra (lb)" },
  { value: "lt", label: "Litro (lt)" },
  { value: "ml", label: "Mililitro (ml)" },
  { value: "m", label: "Metro (m)" },
  { value: "cm", label: "Centímetro (cm)" },
  { value: "caja", label: "Caja" },
  { value: "paquete", label: "Paquete" },
  { value: "docena", label: "Docena" },
  { value: "par", label: "Par" }
];

const emptyForm = {
  codigo: "",
  nombre: "",
  descripcion: "",
  categoria_id: "",
  unidad_medida: "unidad",
  stock_actual: 0,
  stock_minimo: 0,
  precio_costo: 0,
  precio_venta: 0,
  moneda: "RD$",
  ubicacion: "",
  imagen_url: "",
  activo: true
};

export default function InventarioArticulosPage() {
  const [articulos, setArticulos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("all");
  const [stockBajoFilter, setStockBajoFilter] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importFile, setImportFile] = useState(null);
  const [importAnalysis, setImportAnalysis] = useState(null);
  const [importMappings, setImportMappings] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const pageSize = 20;
  const { getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", pageSize);
      if (categoriaFilter !== "all") params.append("categoria_id", categoriaFilter);
      if (stockBajoFilter) params.append("stock_bajo", "true");
      if (searchTerm) params.append("busqueda", searchTerm);
      
      const [articulosRes, categoriasRes] = await Promise.all([
        axios.get(`${API}/inventario/articulos?${params.toString()}`, getAuthHeaders()),
        axios.get(`${API}/inventario/categorias`, getAuthHeaders())
      ]);
      
      const data = articulosRes.data;
      if (data.items) {
        setArticulos(data.items);
        setTotal(data.total || 0);
        setTotalPages(data.pages || 1);
      } else {
        setArticulos(data);
        setTotal(data.length);
        setTotalPages(1);
      }
      setCategorias(categoriasRes.data);
      
      const urlSearch = searchParams.get("search");
      const urlStockBajo = searchParams.get("stock_bajo");
      if (urlSearch && !searchTerm) setSearchTerm(urlSearch);
      if (urlStockBajo === "true" && !stockBajoFilter) setStockBajoFilter(true);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar artículos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, searchParams, page, categoriaFilter, stockBajoFilter, searchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBarcodeScanned = async (codigo) => {
    try {
      const response = await axios.get(`${API}/inventario/articulos/codigo/${codigo}`, getAuthHeaders());
      if (response.data) {
        setSearchTerm(codigo);
        toast.success(`Artículo encontrado: ${response.data.nombre}`);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info("Artículo no encontrado. ¿Desea crearlo?", {
          action: {
            label: "Crear",
            onClick: () => {
              setFormData({ ...emptyForm, codigo });
              setEditingItem(null);
              setShowDialog(true);
            }
          }
        });
      }
    }
  };

  const handleSave = async () => {
    if (!formData.codigo.trim() || !formData.nombre.trim()) {
      toast.error("Código y nombre son requeridos");
      return;
    }

    try {
      setSaving(true);
      if (editingItem) {
        await axios.put(`${API}/inventario/articulos/${editingItem.id}`, formData, getAuthHeaders());
        toast.success("Artículo actualizado");
      } else {
        await axios.post(`${API}/inventario/articulos`, formData, getAuthHeaders());
        toast.success("Artículo creado");
      }
      setShowDialog(false);
      setFormData(emptyForm);
      setEditingItem(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      codigo: item.codigo || "",
      nombre: item.nombre || "",
      descripcion: item.descripcion || "",
      categoria_id: item.categoria_id || "",
      unidad_medida: item.unidad_medida || "unidad",
      stock_actual: item.stock_actual || 0,
      stock_minimo: item.stock_minimo || 0,
      precio_costo: item.precio_costo || 0,
      precio_venta: item.precio_venta || 0,
      moneda: item.moneda || "RD$",
      ubicacion: item.ubicacion || "",
      imagen_url: item.imagen_url || "",
      activo: item.activo !== false
    });
    setShowDialog(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar el artículo "${item.nombre}"?`)) return;
    
    try {
      await axios.delete(`${API}/inventario/articulos/${item.id}`, getAuthHeaders());
      toast.success("Artículo eliminado");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === displayedArticulos.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(displayedArticulos.map(item => item.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error("Seleccione al menos un artículo");
      return;
    }
    
    if (!window.confirm(`¿Eliminar ${selectedItems.length} artículo(s) seleccionado(s)?`)) return;
    
    try {
      setBulkActionLoading(true);
      await axios.post(`${API}/inventario/articulos/bulk-delete`, { articulo_ids: selectedItems }, getAuthHeaders());
      toast.success(`${selectedItems.length} artículo(s) eliminado(s)`);
      setSelectedItems([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar artículos");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleResetStock = async (onlySelected = false) => {
    const count = onlySelected ? selectedItems.length : total;
    const msg = onlySelected 
      ? `¿Resetear stock a 0 para ${selectedItems.length} artículo(s) seleccionado(s)?`
      : `¿Resetear stock a 0 para TODOS los artículos (${count})?`;
    
    if (!window.confirm(msg)) return;
    
    try {
      setBulkActionLoading(true);
      const payload = onlySelected ? { articulo_ids: selectedItems } : {};
      await axios.post(`${API}/inventario/articulos/reset-stock`, payload, getAuthHeaders());
      toast.success(`Stock reseteado a 0`);
      setSelectedItems([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al resetear stock");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Por favor selecciona un archivo Excel (.xlsx o .xls)");
      return;
    }
    
    setImportFile(file);
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/inventario/articulos/import/analyze`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      setImportAnalysis(response.data);
      setImportMappings(response.data.suggested_mappings || []);
      setImportStep(2);
    } catch (err) {
      console.error("Error analyzing file:", err);
      toast.error(err.response?.data?.detail || "Error al analizar el archivo");
    } finally {
      setImporting(false);
    }
  };

  const updateMapping = (excelColumn, targetField) => {
    setImportMappings(prev => prev.map(m => 
      m.excel_column === excelColumn 
        ? { ...m, target_field: targetField === "none" ? null : targetField }
        : m
    ));
  };

  const executeImport = async () => {
    if (!importFile) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('mappings_json', JSON.stringify(importMappings));
      formData.append('skip_duplicates', 'true');
      
      const response = await axios.post(`${API}/inventario/articulos/import/execute`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      setImportStep(3);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} artículos importados exitosamente`);
        fetchData();
      }
    } catch (err) {
      console.error("Error importing:", err);
      toast.error(err.response?.data?.detail || "Error al importar artículos");
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportStep(1);
    setImportFile(null);
    setImportAnalysis(null);
    setImportMappings([]);
    setImportResult(null);
  };

  const closeImportDialog = () => {
    setShowImportDialog(false);
    resetImport();
  };

  const displayedArticulos = articulos;

  const getCategoriaName = (id) => {
    const cat = categorias.find(c => c.id === id);
    return cat?.nombre || "-";
  };

  const formatCurrency = (value, currency = "RD$") => {
    if (currency === "US$") {
      return new Intl.NumberFormat("es-DO", { style: "currency", currency: "USD" }).format(value || 0);
    }
    return `RD$ ${new Intl.NumberFormat("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)}`;
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="articulos-inventario-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Box className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Artículos
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Catálogo de productos en inventario
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-excel-btn">
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </Button>
            <Button 
              variant="outline" 
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/inventario/articulos/exportar`, {
                    ...getAuthHeaders(),
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `inventario_articulos_${new Date().toISOString().split('T')[0]}.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success("Exportación completada");
                } catch (err) {
                  toast.error("Error al exportar");
                }
              }}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exportar Excel
            </Button>
            <Button onClick={() => { setEditingItem(null); setFormData(emptyForm); setShowDialog(true); }} data-testid="new-article-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Artículo
            </Button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedItems.length > 0 && (
          <Card className="mb-4 border-blue-200 bg-blue-50">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <span className="text-blue-700 font-medium">
                  {selectedItems.length} artículo(s) seleccionado(s)
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResetStock(true)}
                    disabled={bulkActionLoading}
                    data-testid="reset-selected-stock-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Resetear Stock Seleccionados
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    data-testid="bulk-delete-btn"
                  >
                    {bulkActionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Eliminar Seleccionados
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedItems([])}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Global Reset Stock Button */}
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            onClick={() => handleResetStock(false)}
            disabled={bulkActionLoading || total === 0}
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
            data-testid="reset-all-stock-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Resetear Stock de Todos ({total})
          </Button>
        </div>

        {/* Barcode Scanner */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Barcode className="w-5 h-5 text-slate-600" />
              <span className="font-medium">Escanear Código de Barras</span>
            </div>
            <BarcodeScanner onScan={handleBarcodeScanned} placeholder="Escanea o ingresa el código del artículo..." />
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por código, nombre..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                    className="pl-10"
                    data-testid="search-articles"
                  />
                </div>
              </div>
              <div className="w-48">
                <Select value={categoriaFilter} onValueChange={(v) => { setCategoriaFilter(v); setPage(0); }}>
                  <SelectTrigger data-testid="filter-categoria">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    {categorias.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant={stockBajoFilter ? "default" : "outline"}
                onClick={() => { setStockBajoFilter(!stockBajoFilter); setPage(0); }}
                className={stockBajoFilter ? "bg-red-600 hover:bg-red-700" : ""}
                data-testid="filter-stock-bajo"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Stock Bajo
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Card>
            <ScrollArea className="h-[calc(100vh-400px)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedItems.length === displayedArticulos.length && displayedArticulos.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead className="text-right">Precio Costo</TableHead>
                    <TableHead className="text-right">Precio Venta</TableHead>
                    <TableHead>Ubicación</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedArticulos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p className="text-slate-500">No se encontraron artículos</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedArticulos.map((item) => (
                      <TableRow key={item.id} className={`${item.stock_actual <= item.stock_minimo && item.stock_minimo > 0 ? "bg-red-50" : ""} ${selectedItems.includes(item.id) ? "bg-blue-50" : ""}`} data-testid={`article-row-${item.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedItems.includes(item.id)}
                            onCheckedChange={() => toggleSelectItem(item.id)}
                            data-testid={`select-${item.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono font-medium">{item.codigo}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.nombre}</p>
                            {item.descripcion && (
                              <p className="text-xs text-slate-500 truncate max-w-xs">{item.descripcion}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getCategoriaName(item.categoria_id)}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {item.stock_actual <= item.stock_minimo && item.stock_minimo > 0 && (
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                            )}
                            <span className={item.stock_actual <= item.stock_minimo && item.stock_minimo > 0 ? "text-red-600 font-bold" : ""}>
                              {item.stock_actual}
                            </span>
                            <span className="text-slate-400 text-sm">/ {item.stock_minimo} {item.unidad_medida}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(item.precio_costo, item.moneda)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">{formatCurrency(item.precio_venta, item.moneda)}</TableCell>
                        <TableCell>{item.ubicacion || "-"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(item)} data-testid={`edit-article-${item.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} data-testid={`delete-article-${item.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-slate-500">
                Mostrando {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} de {total} artículos
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Página {page + 1} de {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingItem ? "Editar Artículo" : "Nuevo Artículo"}</DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  placeholder="Código de barras o SKU"
                  className="font-mono"
                  data-testid="input-codigo"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre del artículo"
                  data-testid="input-nombre"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  placeholder="Descripción del artículo"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={formData.categoria_id || "none"} onValueChange={(v) => setFormData({ ...formData, categoria_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="select-categoria">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin categoría</SelectItem>
                    {categorias.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unidad de Medida</Label>
                <Select value={formData.unidad_medida} onValueChange={(v) => setFormData({ ...formData, unidad_medida: v })}>
                  <SelectTrigger data-testid="select-unidad">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIDADES_MEDIDA.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Stock Actual</Label>
                <Input
                  type="number"
                  value={formData.stock_actual}
                  onChange={(e) => setFormData({ ...formData, stock_actual: parseFloat(e.target.value) || 0 })}
                  min={0}
                  disabled={editingItem}
                  data-testid="input-stock-actual"
                />
                {editingItem && <p className="text-xs text-slate-500">Use entradas/salidas para modificar stock</p>}
              </div>
              <div className="space-y-2">
                <Label>Stock Mínimo</Label>
                <Input
                  type="number"
                  value={formData.stock_minimo}
                  onChange={(e) => setFormData({ ...formData, stock_minimo: parseFloat(e.target.value) || 0 })}
                  min={0}
                  data-testid="input-stock-minimo"
                />
              </div>
              <div className="space-y-2">
                <Label>Moneda</Label>
                <Select value={formData.moneda} onValueChange={(v) => setFormData({ ...formData, moneda: v })}>
                  <SelectTrigger data-testid="moneda-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RD$">RD$ (Peso Dominicano)</SelectItem>
                    <SelectItem value="US$">US$ (Dólar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Precio Costo ({formData.moneda})</Label>
                <Input
                  type="number"
                  value={formData.precio_costo}
                  onChange={(e) => setFormData({ ...formData, precio_costo: parseFloat(e.target.value) || 0 })}
                  min={0}
                  step={0.01}
                  data-testid="input-precio-costo"
                />
              </div>
              <div className="space-y-2">
                <Label>Precio Venta ({formData.moneda})</Label>
                <Input
                  type="number"
                  value={formData.precio_venta}
                  onChange={(e) => setFormData({ ...formData, precio_venta: parseFloat(e.target.value) || 0 })}
                  min={0}
                  step={0.01}
                  data-testid="input-precio-venta"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Ubicación en Almacén</Label>
                <Input
                  value={formData.ubicacion}
                  onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                  placeholder="Ej: Estante A, Fila 3"
                  data-testid="input-ubicacion"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-article-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingItem ? "Guardar Cambios" : "Crear Artículo"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={(open) => { if (!open) closeImportDialog(); else setShowImportDialog(true); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-500" />
                Importar Artículos desde Excel
              </DialogTitle>
            </DialogHeader>

            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${importStep >= 1 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-500 text-white text-xs">1</span>
                Subir Archivo
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${importStep >= 2 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs ${importStep >= 2 ? 'bg-blue-500 text-white' : 'bg-slate-300 text-slate-600'}`}>2</span>
                Mapear Campos
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm ${importStep >= 3 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs ${importStep >= 3 ? 'bg-green-500 text-white' : 'bg-slate-300 text-slate-600'}`}>3</span>
                Resultado
              </div>
            </div>

            {/* Step 1: Upload File */}
            {importStep === 1 && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-600 mb-2">Arrastra un archivo Excel o haz clic para seleccionar</p>
                  <p className="text-xs text-slate-500 mb-4">Formatos aceptados: .xlsx, .xls</p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="excel-upload"
                    data-testid="excel-file-input"
                  />
                  <label htmlFor="excel-upload">
                    <Button asChild disabled={importing}>
                      <span>
                        {importing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analizando...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Seleccionar Archivo
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Formato esperado:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>La primera fila debe contener los encabezados</li>
                    <li>Campos requeridos: <strong>Código</strong> y <strong>Nombre</strong></li>
                    <li>Campos opcionales: Descripción, Categoría, Stock, Precios, etc.</li>
                    <li>La IA analizará las columnas y sugerirá el mapeo automáticamente</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Step 2: Mapping */}
            {importStep === 2 && importAnalysis && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-700">
                    <Check className="w-4 h-4 inline mr-1" />
                    Archivo analizado: <strong>{importFile?.name}</strong> ({importAnalysis.total_rows} filas de datos)
                  </p>
                </div>

                <p className="text-sm text-slate-600">
                  La IA ha sugerido el mapeo de columnas. Revisa y ajusta si es necesario:
                </p>

                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/3">Columna en Excel</TableHead>
                        <TableHead className="w-1/3">Datos de Ejemplo</TableHead>
                        <TableHead className="w-1/3">Campo de Destino</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importMappings.map((mapping, idx) => {
                        const sampleData = importAnalysis.sample_data?.find(s => s.header === mapping.excel_column);
                        return (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{mapping.excel_column}</TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {sampleData?.samples?.slice(0, 2).join(", ") || "-"}
                            </TableCell>
                            <TableCell>
                              <Select 
                                value={mapping.target_field || "none"} 
                                onValueChange={(v) => updateMapping(mapping.excel_column, v)}
                              >
                                <SelectTrigger className={mapping.target_field ? "border-green-300" : "border-slate-200"}>
                                  <SelectValue placeholder="No importar" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No importar</SelectItem>
                                  {Object.entries(importAnalysis.target_fields || {}).map(([key, info]) => (
                                    <SelectItem key={key} value={key}>
                                      {key} {info.requerido ? "(requerido)" : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {mapping.confidence && mapping.target_field && (
                                <p className="text-xs text-slate-500 mt-1">
                                  Confianza: {Math.round(mapping.confidence * 100)}% - {mapping.reason}
                                </p>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {!importMappings.some(m => m.target_field === "codigo") && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-amber-700 text-sm">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Advertencia: No hay columna mapeada a &quot;código&quot; (campo requerido)
                    </p>
                  </div>
                )}
                {!importMappings.some(m => m.target_field === "nombre") && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-amber-700 text-sm">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Advertencia: No hay columna mapeada a &quot;nombre&quot; (campo requerido)
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Result */}
            {importStep === 3 && importResult && (
              <div className="space-y-4">
                <div className={`p-6 rounded-lg text-center ${importResult.imported > 0 ? 'bg-green-50' : 'bg-amber-50'}`}>
                  {importResult.imported > 0 ? (
                    <Check className="w-12 h-12 mx-auto text-green-500 mb-3" />
                  ) : (
                    <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
                  )}
                  <h3 className="text-xl font-bold mb-2">
                    {importResult.imported > 0 ? "Importación Completada" : "Importación con Advertencias"}
                  </h3>
                  <div className="flex justify-center gap-6 text-sm">
                    <div className="text-green-700">
                      <span className="font-bold text-2xl">{importResult.imported}</span>
                      <p>Importados</p>
                    </div>
                    <div className="text-amber-700">
                      <span className="font-bold text-2xl">{importResult.skipped}</span>
                      <p>Omitidos (duplicados)</p>
                    </div>
                    <div className="text-red-700">
                      <span className="font-bold text-2xl">{importResult.total_errors}</span>
                      <p>Errores</p>
                    </div>
                  </div>
                </div>

                {importResult.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-800 mb-2">Errores encontrados:</h4>
                    <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                      {importResult.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              {importStep === 1 && (
                <Button variant="outline" onClick={closeImportDialog}>Cancelar</Button>
              )}
              {importStep === 2 && (
                <>
                  <Button variant="outline" onClick={resetImport}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Subir Otro Archivo
                  </Button>
                  <Button 
                    onClick={executeImport} 
                    disabled={importing || !importMappings.some(m => m.target_field === "codigo") || !importMappings.some(m => m.target_field === "nombre")}
                    data-testid="execute-import-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Ejecutar Importación
                      </>
                    )}
                  </Button>
                </>
              )}
              {importStep === 3 && (
                <Button onClick={closeImportDialog}>Cerrar</Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
